import { css } from '@emotion/react';
import { LOADER_COLOR_VARIABLE } from '../loader';
import { cssVariableWithOverride, fontOverrides } from '../overrides';
import { darkThemeStyle, theme } from '../theme';
import { tokens } from '../tokens';
const sizeMap = {
    large: {
        counterFontSize: tokens.fontSizes.medium,
        fontSize: tokens.fontSizes.large,
        sizing: tokens.sizing.large,
        spacing: tokens.spacing.large,
    },
    medium: {
        counterFontSize: `calc(${cssVariableWithOverride(fontOverrides.buttonMedium, tokens.fontSizes.medium)} - 2px)`,
        fontSize: cssVariableWithOverride(fontOverrides.buttonMedium, tokens.fontSizes.medium),
        sizing: tokens.sizing.medium,
        spacing: tokens.spacing.medium,
    },
    small: {
        counterFontSize: tokens.fontSizes.xsmall,
        fontSize: tokens.fontSizes.small,
        sizing: tokens.sizing.small,
        spacing: tokens.spacing.small,
    },
};
export function getThemedStyle({ variant, }) {
    switch (variant) {
        case 'primary':
            return {
                backgroundColor: theme.brand.main,
                color: theme.brand.textOnColor,
                hoverBackgroundColor: theme.background.hoverStrong,
            };
        case 'plain':
            return {
                backgroundColor: 'transparent',
                color: theme.text.link,
                hoverBackgroundColor: theme.background.hover,
            };
        case 'destructive':
            return {
                backgroundColor: theme.error.main,
                color: theme.error.textOnColor,
                hoverBackgroundColor: theme.background.hoverStrong,
            };
        case 'upgrade':
            return {
                backgroundColor: theme.upgrade.main,
                color: theme.upgrade.textOnColor,
                hoverBackgroundColor: theme.background.hoverStrong,
            };
        case 'secondary':
        default:
            return {
                backgroundColor: 'transparent',
                borderColor: theme.border.interactive,
                color: theme.text.main,
                hoverBackgroundColor: theme.background.hover,
            };
    }
}
const buttonLoadingStaticStyle = css({
    // Hide all direct descendants, except the wrapper for the loader
    '& > *:not(:last-child)': {
        height: 0,
        opacity: 0,
    },
});
const buttonStaticStyle = css({
    '::before': {
        borderRadius: '2px',
        content: '""',
        display: 'block',
        height: '100%',
        inset: 0,
        position: 'absolute',
        width: '100%',
        zIndex: 0,
    },
    alignItems: 'center',
    borderColor: 'transparent',
    borderRadius: tokens.borderRadius.medium,
    borderStyle: 'solid',
    borderWidth: tokens.borderWidth.medium,
    cursor: 'pointer',
    display: 'inline-flex',
    flexDirection: 'column',
    flexShrink: 0,
    fontFamily: tokens.fontFamilies.sansSerif,
    fontWeight: tokens.fontWeights.bold,
    justifyContent: 'center',
    lineHeight: tokens.lineHeights.tight,
    margin: 0,
    outline: 0,
    padding: 0,
    position: 'relative',
    textDecoration: 'none',
    transition: 'background-color 125ms ease-out',
    userSelect: 'none',
    verticalAlign: 'middle',
});
// Generate button element style based on various options
export function buttonStyle({ fullWidth, inverted, isFocusVisible, isIconOnly, isLoading, size, themedStyle, }) {
    const hasBorder = themedStyle.borderColor != null;
    return [
        ...(inverted ? [darkThemeStyle] : []),
        buttonStaticStyle,
        css(Object.assign(Object.assign(Object.assign(Object.assign({ backgroundColor: themedStyle.backgroundColor, color: themedStyle.color, fontSize: sizeMap[size].fontSize, height: sizeMap[size].sizing, minWidth: sizeMap[size].sizing, width: fullWidth ? '100%' : 'auto' }, (!isIconOnly && {
            paddingLeft: sizeMap[size].spacing,
            paddingRight: sizeMap[size].spacing,
        })), (hasBorder && {
            '::before': {
                borderRadius: '2px',
                margin: 0,
            },
            borderColor: themedStyle.borderColor,
        })), { ':active': {
                backgroundColor: themedStyle.backgroundColor,
            }, ':disabled': {
                cursor: 'default',
                opacity: tokens.opacity.medium,
                pointerEvents: 'none',
            }, ':hover': Object.assign(Object.assign({}, (!hasBorder && {
                borderColor: themedStyle.hoverBackgroundColor,
            })), { '::before': {
                    backgroundColor: themedStyle.hoverBackgroundColor,
                } }), '> *': {
                zIndex: tokens.zIndex.default,
            } }), (isFocusVisible && {
            outline: `2px solid ${theme.border.focusOutline}`,
            outlineOffset: 2,
        }))),
        ...(isLoading ? [buttonLoadingStaticStyle] : []),
    ];
}
const loaderWrapperStaticStyle = css({
    // Override for child opacity from parent
    '&&': {
        opacity: 1,
    },
    alignItems: 'center',
    display: 'flex',
});
export function loaderWrapperStyle({ hasLoadingLabel, size, }) {
    return [
        loaderWrapperStaticStyle,
        ...(hasLoadingLabel
            ? [
                css({
                    span: {
                        paddingLeft: size === 'small' ? tokens.spacing.xsmall : tokens.spacing.small,
                    },
                }),
            ]
            : []),
    ];
}
export function loaderStyle({ themedStyle }) {
    return css({
        [LOADER_COLOR_VARIABLE]: themedStyle.color,
    });
}
export function loaderTextStyle({ themedStyle }) {
    return css({
        color: themedStyle.color,
    });
}
const innerContentStaticStyle = css({
    alignItems: 'center',
    display: 'flex',
    height: '100%',
});
export function innerContentStyle({ size }) {
    return [
        innerContentStaticStyle,
        css({
            gap: size === 'small' ? tokens.spacing.xsmall : tokens.spacing.small,
        }),
    ];
}
const countStaticStyle = css({
    display: 'flex',
    flexGrow: 1,
    userSelect: 'none',
});
export function countStyle({ size }) {
    return [
        countStaticStyle,
        css({
            fontSize: sizeMap[size].counterFontSize,
        }),
    ];
}
